import { cn } from '@/app/lib/utils';
import CustomSelect, { Option } from '../ui/CustomSelect';
import { useEffect, useMemo, useState } from 'react';
import { getCompanies, getCompany } from '@/api/companies';
import { useMutation, useQuery } from '@tanstack/react-query';
import Avatar from '../ui/Avatar';
import Icon from '../ui/Icon';
import { useParams } from 'next/navigation';

type Props = {
  defaultValue: any;
  label?: string;
  required?: boolean;
  wrapperClassName?: string;
  className?: string;
  searchable?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  placeholder?: string;
  fetchSelectedOptionOnMount?: boolean;
  multiple?: boolean;
  onOptionSelect: (option: any) => void;
  varient?: 'default' | 'inline-multiple';
  /** When provided, skips the internal API fetch and uses these options directly */
  options?: Option[];
  /** External loading state (used alongside the `options` prop) */
  isOptionsLoading?: boolean;
  /** Called when the search query changes (used alongside the `options` prop) */
  onSearch?: (search: string) => void;
  filterParams?: Record<string, string>;
};

const CompanySelect = ({
  defaultValue,
  label,
  required,
  wrapperClassName,
  className,
  searchable,
  clearable,
  disabled,
  placeholder,
  onOptionSelect,
  fetchSelectedOptionOnMount,
  multiple,
  varient,
  options: externalOptions,
  isOptionsLoading,
  onSearch: onExternalSearch,
  filterParams,
}: Props) => {
  const [companySearch, setCompanySearch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<Option | null>(null);
  const [selectedCompanies, setSelectedCompanies] = useState<Option[]>([]);
  const [isOptionCbTriggered, setIsOptionCbTriggered] = useState(false);
  const { clientId } = useParams<{ clientId: string }>();
  const { data: companies, isLoading: isLoadingCompanies } = useQuery({
    queryKey: ['companies', clientId, companySearch, filterParams],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filterParams) {
        Object.keys(filterParams).forEach(key => {
          params.set(key, filterParams[key]);
        });
      }

      return getCompanies({
        clientId: clientId || '',
        search: companySearch,
        filterSearchParams: params,
      });
    },
    enabled: !!clientId && !externalOptions,
  });
  const {
    mutate: getCompanyMutation,
    data: missingCompany,
    isPending: isLoadingMissingCompany,
  } = useMutation({
    mutationFn: (id: string) => getCompany(clientId || '', id),
    onError: error => {
      console.error('error', error);
    },
  });

  const companyOptions = useMemo(() => {
    // Use external options when provided
    if (externalOptions) return externalOptions;

    const list: Option[] =
      companies?.data?.map(company => ({
        id: company.id,
        value: company.name.full || company.name.short || '-',
        prefix: (
          <div className="mr-2">
            <Avatar
              size={32}
              name={company.name.full || company.name.short}
              src={company.logo}
              isExternalUrl
              initialsClassName="text-xs rounded-sm"
              className="rounded-sm"
              imageClassName="rounded-sm"
            />
          </div>
        ),
      })) || [];
    if (!multiple && selectedCompany && list.find(o => o.id === selectedCompany.id) === undefined) {
      list.unshift({
        id: selectedCompany.id.toString(),
        value: selectedCompany.value.toString(),
        prefix: selectedCompany.prefix,
      });
    }
    if (multiple && selectedCompanies) {
      // find missing options
      const missingOptions = selectedCompanies.filter(o => !list.some(c => c.id === o.id));
      list.unshift(...missingOptions);
    }
    if (missingCompany) {
      list.unshift({
        id: missingCompany.id,
        value: missingCompany.name.full || missingCompany.name.short || '-',
        prefix: (
          <div className="mr-2">
            <Avatar
              size={32}
              name={missingCompany.name.full || missingCompany.name.short}
              src={missingCompany.logo}
              isExternalUrl
              initialsClassName="text-xs rounded-sm"
              className="rounded-sm"
              imageClassName="rounded-sm"
            />
          </div>
        ),
      });
    }
    return list;
  }, [companies, selectedCompany, selectedCompanies, multiple, missingCompany]);

  useEffect(() => {
    if (fetchSelectedOptionOnMount && defaultValue && !isOptionCbTriggered && !isLoadingCompanies) {
      let values: Option | Option[] | null = null;
      if (multiple) {
        values = companyOptions.filter(o => defaultValue.includes(o.id)) || [];
      } else {
        values = companyOptions.find(o => o.id === defaultValue) || null;
      }
      if ((Array.isArray(values) && values.length !== 0) || values !== null) {
        onOptionSelect(values);
        setIsOptionCbTriggered(true);
      } else if (defaultValue || (Array.isArray(defaultValue) && defaultValue.length !== 0)) {
        // call api
        getCompanyMutation(defaultValue);
      }
    }
  }, [
    fetchSelectedOptionOnMount,
    defaultValue,
    companyOptions,
    onOptionSelect,
    isOptionCbTriggered,
    isLoadingCompanies,
    multiple,
    getCompanyMutation,
  ]);

  return (
    <div className={cn('flex flex-col', wrapperClassName)}>
      {label && (
        <label className="text-sm font-medium mb-1.5 flex items-center gap-1 text-muted-foreground">
          <span>
            {label}
            {required && <span className="text-red-600 ml-1">*</span>}
          </span>
          {isLoadingMissingCompany && (
            <Icon
              name="loader-2-fill"
              size={4}
              aria-label="Loading"
              className="animate-spin w-4 h-4"
            />
          )}
        </label>
      )}
      <CustomSelect
        options={companyOptions || []}
        placeholder={placeholder}
        defaultValue={defaultValue}
        onOptionSelect={option => {
          console.log('option', option);
          if (multiple) {
            setSelectedCompanies(option as Option[]);
          } else {
            setSelectedCompany(option as Option);
          }
          onOptionSelect(option);
        }}
        searchable={searchable}
        clearable={clearable}
        disabled={disabled}
        triggerClassNames={className}
        onSearch={search => {
          setCompanySearch(search);
          onExternalSearch?.(search);
        }}
        searchLoading={externalOptions ? isOptionsLoading : isLoadingCompanies}
        multiple={multiple}
        varient={varient}
      />
    </div>
  );
};

export default CompanySelect;
